import { z } from 'zod';
import { HhMmSchema, ReportScopeSchema } from './holidays';

/**
 * Phase 3 time & attendance (docs/specs/phase-3-time-attendance.md, D49–D53). Times on the
 * wire are Europe/London wall-clock with no zone: "YYYY-MM-DDTHH:MM:SS" for a clocking and
 * "HH:MM" for a shift time, exactly as the clocks recorded them.
 */

const IsoDate = z.iso.date();
/** A wall-clock instant in Europe/London, no offset (the clocks' own convention). */
export const WallClockSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/, 'Use YYYY-MM-DDTHH:MM:SS');
export const YearMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');

export const AttendanceStatusSchema = z.enum([
  'not_due',
  'due',
  'holiday',
  'absence',
  'bank_holiday',
]);
export type AttendanceStatus = z.infer<typeof AttendanceStatusSchema>;

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  not_due: 'Not due',
  due: 'Due',
  holiday: 'Holiday',
  absence: 'Absence',
  bank_holiday: 'Bank holiday',
};

/** Where a reading's clock-out came from; `auto_closed` and `missing` mean "no real finish" (D50). */
export const OutSourceSchema = z.enum(['device', 'app', 'manual', 'auto_closed', 'missing']);
export type OutSource = z.infer<typeof OutSourceSchema>;

export const ExceptionKindSchema = z.enum([
  'no_show',
  'late_in',
  'early_out',
  'missing_out',
  'unplanned_shift',
  'casual_shift',
  'long_day',
  'duplicate_punch',
]);
export type ExceptionKind = z.infer<typeof ExceptionKindSchema>;

export const EXCEPTION_KIND_LABELS: Record<ExceptionKind, string> = {
  no_show: 'No show',
  late_in: 'Late in',
  early_out: 'Early out',
  missing_out: 'Missing clock-out',
  unplanned_shift: 'Unplanned shift',
  casual_shift: 'Casual shift',
  long_day: 'Long day',
  duplicate_punch: 'Duplicate punch',
};

export const AttendanceSettingsSchema = z.object({
  graceLateMinutes: z.number().int().min(0).max(120),
  graceEarlyMinutes: z.number().int().min(0).max(120),
  longDayMinutes: z.number().int().min(60).max(1440),
});
export type AttendanceSettings = z.infer<typeof AttendanceSettingsSchema>;

export const AttendanceReadingSchema = z.object({
  clockId: z.string(),
  inAt: WallClockSchema,
  /** Null when the clock-out is missing or was the old 23:59 auto-close. */
  outAt: WallClockSchema.nullable(),
  outSource: OutSourceSchema,
  /** The clock-in was keyed by hand in the old T&A screens. */
  manual: z.boolean(),
  siteCode: z.string().nullable(),
});
export type AttendanceReading = z.infer<typeof AttendanceReadingSchema>;

// Reasons and resolutions (M2, D52) ----------------------------------------------------

/** `business` = paid (customer delivery, shift change, system error…); `hr` = unpaid. */
export const ReasonCategorySchema = z.enum(['business', 'hr']);
export type ReasonCategory = z.infer<typeof ReasonCategorySchema>;

export const REASON_CATEGORY_LABELS: Record<ReasonCategory, string> = {
  business: 'Business (paid)',
  hr: 'HR (unpaid)',
};

export const ResolutionSourceSchema = z.enum(['aroo', 'history', 'readback']);
export type ResolutionSource = z.infer<typeof ResolutionSourceSchema>;

export const RESOLUTION_SOURCE_LABELS: Record<ResolutionSource, string> = {
  aroo: 'Aroo',
  history: 'imported from the old system',
  readback: 'accepted in the old system',
};

/** How a resolution is shown next to its exception. */
export const ResolutionSummarySchema = z.object({
  id: z.uuid(),
  reasonCode: z.string(),
  reasonName: z.string(),
  category: ReasonCategorySchema,
  note: z.string().nullable(),
  /** The corrected times (London wall clock) the worked minutes now use. */
  correctedStart: WallClockSchema.nullable(),
  correctedFinish: WallClockSchema.nullable(),
  /** The Phase 2b absence the reason created or extended (no-shows with an absence reason). */
  absenceId: z.uuid().nullable(),
  absenceAction: z.enum(['created', 'extended']).nullable(),
  source: ResolutionSourceSchema,
  /** The resolver's name, or the old system's free-text approver. */
  resolvedBy: z.string().nullable(),
  resolvedAt: z.iso.datetime(),
  /** Its pay period is open or in review (3-M3): it can be undone. */
  canUndo: z.boolean(),
});
export type ResolutionSummary = z.infer<typeof ResolutionSummarySchema>;

export const AttendanceExceptionSchema = z.object({
  /** Null for a day computed live that the nightly recompute has not stored yet. */
  id: z.uuid().nullable(),
  kind: ExceptionKindSchema,
  /** Lost/extra minutes where the kind has them (late, early, no show, casual hours …). */
  minutes: z.number().int().nullable(),
  /** One-line explanation, e.g. "In 08:16, shift 08:00". */
  detail: z.string(),
  status: z.enum(['open', 'resolved']),
  /** False: no longer raised by the facts, shown for its resolution (e.g. a no-show now sickness). */
  active: z.boolean(),
  resolution: ResolutionSummarySchema.nullable(),
});
export type AttendanceException = z.infer<typeof AttendanceExceptionSchema>;

export const ExpectedShiftSchema = z.object({
  start: HhMmSchema.nullable(),
  finish: HhMmSchema.nullable(),
  breakMinutes: z.number().int().nullable(),
  /** finish − start − break; null when the day is untimed. */
  minutes: z.number().int().nullable(),
});
export type ExpectedShift = z.infer<typeof ExpectedShiftSchema>;

export const AttendanceDaySchema = z.object({
  date: IsoDate,
  status: AttendanceStatusSchema,
  /** Half-day holiday/absence: the half that is off (the person is due for the other half). */
  offHalf: z.enum(['am', 'pm']).nullable(),
  /** What the day (or half) off is: "Holiday", the absence type's name, the bank holiday's name. */
  offLabel: z.string().nullable(),
  casual: z.boolean(),
  /** What the pattern expected for the hours the person is due; null when not due. */
  expected: ExpectedShiftSchema.nullable(),
  firstIn: WallClockSchema.nullable(),
  lastOut: WallClockSchema.nullable(),
  outSource: OutSourceSchema.nullable(),
  /**
   * (last out − first in) − break; null when the clock-out is missing or auto-closed. A
   * resolution's corrected start/finish replace the clocked ones here (M2).
   */
  workedMinutes: z.number().int().nullable(),
  /** The corrected start/finish a resolution gave the day, if any. */
  corrected: z
    .object({ start: WallClockSchema.nullable(), finish: WallClockSchema.nullable() })
    .nullable(),
  readings: z.array(AttendanceReadingSchema),
  exceptions: z.array(AttendanceExceptionSchema),
  /** Today or later: not finished, so no no-show or missing clock-out yet. */
  open: z.boolean(),
});
export type AttendanceDay = z.infer<typeof AttendanceDaySchema>;

export const AttendanceMonthResponseSchema = z.object({
  person: z.object({
    id: z.uuid(),
    name: z.string(),
    siteCode: z.string().nullable(),
    siteName: z.string().nullable(),
    jobTitle: z.string().nullable(),
    /** D55: never due, so never a no-show. */
    doesNotClock: z.boolean(),
  }),
  month: YearMonthSchema,
  today: IsoDate,
  days: z.array(AttendanceDaySchema),
  totals: z.object({
    dueDays: z.number(),
    clockedDays: z.number().int(),
    expectedMinutes: z.number().int(),
    workedMinutes: z.number().int(),
    /** Due days so far whose worked time is unknown (missing / auto-closed clock-out). */
    unknownDays: z.number().int(),
    exceptions: z.record(z.string(), z.number().int()),
  }),
  settings: AttendanceSettingsSchema,
});
export type AttendanceMonthResponse = z.infer<typeof AttendanceMonthResponseSchema>;

export const AttendanceMonthQuerySchema = z.object({ month: YearMonthSchema.optional() });

// Today -----------------------------------------------------------------------------

export const TodayStateSchema = z.enum(['in', 'left', 'not_in', 'off', 'not_due']);
export type TodayState = z.infer<typeof TodayStateSchema>;

export const TodayPersonSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  jobTitle: z.string().nullable(),
  state: TodayStateSchema,
  status: AttendanceStatusSchema,
  /** Why the person is off ("Holiday", absence type, bank holiday, "Not due"). */
  offLabel: z.string().nullable(),
  offHalf: z.enum(['am', 'pm']).nullable(),
  casual: z.boolean(),
  expectedStart: HhMmSchema.nullable(),
  expectedFinish: HhMmSchema.nullable(),
  firstIn: WallClockSchema.nullable(),
  lastOut: WallClockSchema.nullable(),
  /** Minutes late when the clock-in is past the grace (D49), else null. */
  lateMinutes: z.number().int().nullable(),
  /** Due, not in, and the shift started more than the grace ago. */
  overdue: z.boolean(),
});
export type TodayPerson = z.infer<typeof TodayPersonSchema>;

export const TodayCountsSchema = z.object({
  due: z.number().int(),
  in: z.number().int(),
  left: z.number().int(),
  notIn: z.number().int(),
  off: z.number().int(),
});
export type TodayCounts = z.infer<typeof TodayCountsSchema>;

export const MissingOutSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  siteCode: z.string().nullable(),
  /** The home branch as people read it ("Port Talbot"), so screens never show the code. */
  siteName: z.string().nullable(),
  date: IsoDate,
  firstIn: WallClockSchema.nullable(),
  outSource: OutSourceSchema.nullable(),
  expectedFinish: HhMmSchema.nullable(),
});
export type MissingOut = z.infer<typeof MissingOutSchema>;

export const AttendanceTodayResponseSchema = z.object({
  date: IsoDate,
  /** London wall-clock time the answer was computed at. */
  asOf: WallClockSchema,
  scope: ReportScopeSchema,
  totals: TodayCountsSchema,
  sites: z.array(
    z.object({
      siteId: z.uuid(),
      code: z.string(),
      name: z.string(),
      counts: TodayCountsSchema,
      people: z.array(TodayPersonSchema),
    }),
  ),
  /** Yesterday's days whose clock-out is missing or was auto-closed (D50). */
  missingOuts: z.array(MissingOutSchema),
  missingOutsDate: IsoDate,
});
export type AttendanceTodayResponse = z.infer<typeof AttendanceTodayResponseSchema>;

export const AttendanceScopeQuerySchema = z.object({
  site: z.string().trim().max(32).optional(),
  region: z.string().trim().max(32).optional(),
});

// Exceptions list ------------------------------------------------------------------

export const AttendanceExceptionsQuerySchema = AttendanceScopeQuerySchema.extend({
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  kind: ExceptionKindSchema.optional(),
  status: z.enum(['open', 'resolved', 'all']).optional(),
});
export type AttendanceExceptionsQuery = z.infer<typeof AttendanceExceptionsQuerySchema>;

export const AttendanceExceptionItemSchema = z.object({
  id: z.uuid(),
  personId: z.uuid(),
  name: z.string(),
  siteCode: z.string().nullable(),
  /** The home branch as people read it ("Port Talbot"), so screens never show the code. */
  siteName: z.string().nullable(),
  date: IsoDate,
  kind: ExceptionKindSchema,
  minutes: z.number().int().nullable(),
  detail: z.string(),
  status: z.enum(['open', 'resolved']),
  /** False when the facts changed and the engine no longer raises it (kept for its resolution). */
  active: z.boolean(),
  firstSeenAt: z.iso.datetime(),
  /** The day as stored, for the resolve form (default finish, clocked in). */
  firstIn: WallClockSchema.nullable(),
  lastOut: WallClockSchema.nullable(),
  expectedStart: HhMmSchema.nullable(),
  expectedFinish: HhMmSchema.nullable(),
  expectedBreak: z.number().int().nullable(),
  workedMinutes: z.number().int().nullable(),
  resolution: ResolutionSummarySchema.nullable(),
});
export type AttendanceExceptionItem = z.infer<typeof AttendanceExceptionItemSchema>;

export const AttendanceExceptionsResponseSchema = z.object({
  from: IsoDate,
  to: IsoDate,
  scope: ReportScopeSchema,
  /** Per kind over the whole filter (before the kind filter and the row limit). */
  counts: z.record(z.string(), z.number().int()),
  total: z.number().int(),
  items: z.array(AttendanceExceptionItemSchema),
  /** More rows matched than the limit returned. */
  truncated: z.boolean(),
  /** When the clocks were last read from the old system (null if never). */
  lastSyncAt: z.iso.datetime().nullable(),
});
export type AttendanceExceptionsResponse = z.infer<typeof AttendanceExceptionsResponseSchema>;

export const AttendanceSettingsResponseSchema = z.object({
  settings: AttendanceSettingsSchema,
  lastSyncAt: z.iso.datetime().nullable(),
});
export type AttendanceSettingsResponse = z.infer<typeof AttendanceSettingsResponseSchema>;

/** PATCH /attendance/settings (attendance.admin.manage): any of the three; audited. */
export const AttendanceSettingsPatchSchema = AttendanceSettingsSchema.partial().refine(
  (v) => Object.keys(v).length > 0,
  { message: 'Change at least one setting' },
);
export type AttendanceSettingsPatch = z.infer<typeof AttendanceSettingsPatchSchema>;

export const AttendanceSettingsPatchResponseSchema = z.object({
  settings: AttendanceSettingsSchema,
  /** A full recompute (13 months, everyone) was started in the background. */
  recomputeStarted: z.boolean(),
});
export type AttendanceSettingsPatchResponse = z.infer<typeof AttendanceSettingsPatchResponseSchema>;

// Reasons catalogue (D52) --------------------------------------------------------------

export const ReasonCodeSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{1,39}$/, 'Lower-case letters, digits and _, 2–40 long');

export const AttendanceReasonSchema = z.object({
  code: z.string(),
  name: z.string(),
  category: ReasonCategorySchema,
  /** A Phase 2b absence type: resolving a no-show with it records that absence for the day. */
  absenceTypeCode: z.string().nullable(),
  absenceTypeName: z.string().nullable(),
  /** The exception kinds it is offered for. */
  appliesTo: z.array(ExceptionKindSchema),
  /** Always ask for the real finish time, whatever the kind. */
  needsFinishTime: z.boolean(),
  active: z.boolean(),
  sort: z.number().int(),
  /** The old system's names for it (history import and the nightly read-back match on these). */
  legacyNames: z.array(z.string()),
  /** Resolutions that use it (any source). */
  inUse: z.number().int(),
});
export type AttendanceReason = z.infer<typeof AttendanceReasonSchema>;

export const AttendanceReasonsResponseSchema = z.object({
  reasons: z.array(AttendanceReasonSchema),
});
export type AttendanceReasonsResponse = z.infer<typeof AttendanceReasonsResponseSchema>;

const ReasonName = z.string().trim().min(2).max(100);
const LegacyNames = z.array(z.string().trim().min(1).max(100)).max(50);

/** POST /attendance/reasons */
export const AttendanceReasonCreateSchema = z.object({
  code: ReasonCodeSchema,
  name: ReasonName,
  category: ReasonCategorySchema,
  absenceTypeCode: z.string().trim().max(32).nullable().optional(),
  appliesTo: z.array(ExceptionKindSchema).min(1),
  needsFinishTime: z.boolean().optional(),
  active: z.boolean().optional(),
  sort: z.number().int().min(0).max(9999).optional(),
  legacyNames: LegacyNames.optional(),
});
export type AttendanceReasonCreate = z.infer<typeof AttendanceReasonCreateSchema>;

/** PATCH /attendance/reasons/:code (never deleted: `active: false` retires it). */
export const AttendanceReasonPatchSchema = z
  .object({
    name: ReasonName.optional(),
    category: ReasonCategorySchema.optional(),
    absenceTypeCode: z.string().trim().max(32).nullable().optional(),
    appliesTo: z.array(ExceptionKindSchema).min(1).optional(),
    needsFinishTime: z.boolean().optional(),
    active: z.boolean().optional(),
    sort: z.number().int().min(0).max(9999).optional(),
    legacyNames: LegacyNames.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Change at least one field' });
export type AttendanceReasonPatch = z.infer<typeof AttendanceReasonPatchSchema>;

export const AttendanceReasonWriteResponseSchema = z.object({ reason: AttendanceReasonSchema });
export type AttendanceReasonWriteResponse = z.infer<typeof AttendanceReasonWriteResponseSchema>;

// Resolve ----------------------------------------------------------------------------------

/** POST /attendance/exceptions/:id/resolve. Times are "HH:MM" on the exception's day (a finish
 * before the start is the next morning). */
export const ResolveExceptionSchema = z.object({
  reasonCode: z.string().trim().min(1).max(40),
  note: z.string().trim().max(500).optional(),
  correctedFinish: HhMmSchema.optional(),
  correctedStart: HhMmSchema.optional(),
});
export type ResolveException = z.infer<typeof ResolveExceptionSchema>;

export const ResolveResponseSchema = z.object({
  exceptionId: z.uuid(),
  resolution: ResolutionSummarySchema,
});
export type ResolveResponse = z.infer<typeof ResolveResponseSchema>;

/** POST /attendance/exceptions/resolve: the same reason for many (no corrected times). */
export const BulkResolveSchema = z.object({
  ids: z.array(z.uuid()).min(1).max(500),
  reasonCode: z.string().trim().min(1).max(40),
  note: z.string().trim().max(500).optional(),
});
export type BulkResolve = z.infer<typeof BulkResolveSchema>;

export const BulkResolveResponseSchema = z.object({
  resolved: z.array(z.uuid()),
  failed: z.array(z.object({ id: z.uuid(), error: z.string(), message: z.string() })),
});
export type BulkResolveResponse = z.infer<typeof BulkResolveResponseSchema>;

export const UndoResolutionSchema = z.object({ note: z.string().trim().max(500).optional() });
export type UndoResolution = z.infer<typeof UndoResolutionSchema>;

export const UndoResolutionResponseSchema = z.object({
  ok: z.literal(true),
  exceptionId: z.uuid().nullable(),
  /** What happened to the absence the resolution created/extended. */
  absence: z.enum(['cancelled', 'shortened', 'none']),
});
export type UndoResolutionResponse = z.infer<typeof UndoResolutionResponseSchema>;

// Person flags (D55) ------------------------------------------------------------------------

export const AttendanceFlagsSchema = z.object({
  personId: z.uuid(),
  /** D55: never due (RM, director, MD seats and head office by default). */
  doesNotClock: z.boolean(),
  /** This holiday year's casual / zero-hours flag; null when HR has not set the year up. */
  casual: z.boolean().nullable(),
  casualYear: z.number().int(),
});
export type AttendanceFlags = z.infer<typeof AttendanceFlagsSchema>;

/** PUT /attendance/people/:id/flags (people.person.edit at the person's site). */
export const AttendanceFlagsPutSchema = z
  .object({ doesNotClock: z.boolean().optional(), casual: z.boolean().optional() })
  .refine((v) => v.doesNotClock !== undefined || v.casual !== undefined, {
    message: 'Change at least one flag',
  });
export type AttendanceFlagsPut = z.infer<typeof AttendanceFlagsPutSchema>;

// Rules shared by the API and the web ------------------------------------------------------

/** The kinds whose resolution must say when the person really finished (D50, D53). */
export function resolveNeedsFinish(
  kind: ExceptionKind,
  exceptionMinutes: number | null,
  reason: { needsFinishTime: boolean },
): boolean {
  if (reason.needsFinishTime) return true;
  if (kind === 'missing_out') return true;
  // A casual shift with a real clock-out already has its hours; one without needs the finish.
  return kind === 'casual_shift' && exceptionMinutes === null;
}

/** Is the reason offered for this kind (active and listed in applies-to)? */
export function reasonApplies(
  reason: { active: boolean; appliesTo: readonly ExceptionKind[] },
  kind: ExceptionKind,
): boolean {
  return reason.active && reason.appliesTo.includes(kind);
}

/**
 * The first day of the implicit open window: the current calendar month and the previous one.
 * Since 3-M3 a month's pay period decides (attendance/periods.ts); this is the default for a
 * month HR has not acted on yet.
 */
export function openPeriodStart(today: string): string {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const py = m === 1 ? y - 1 : y;
  const pm = m === 1 ? 12 : m - 1;
  return `${py}-${String(pm).padStart(2, '0')}-01`;
}
